import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

// The selected panel only becomes the scroll owner when it keeps a usable
// share of the viewport below the chrome (header, alerts, status band, an open
// overview, tabs). Otherwise the document scrolls, as on phones.
const MIN_PANEL_PX = 256;
const MIN_PANEL_SHARE = 0.45;
// Extra room required to switch back to the panel, so a layout that sits on
// the threshold (e.g. a scrollbar appearing) cannot flip-flop.
const HYSTERESIS_PX = 24;

const toPx = (value, rootFontSize) => {
  const number = parseFloat(value);
  if (!Number.isFinite(number)) return 0;
  return String(value).trim().endsWith('rem') ? number * rootFontSize : number;
};

/**
 * Space (px) the selected panel would get if the page were sized to the
 * viewport. AdminLayout publishes the shell clearance above and below the
 * page as --admin-content-top / --admin-content-bottom.
 */
export const measureWorkspace = (page) => {
  const panel = page?.querySelector(':scope > [data-cockpit-panel]');
  if (!panel) return null;

  const styles = getComputedStyle(page);
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const available = window.innerHeight
    - toPx(styles.getPropertyValue('--admin-content-top'), rootFontSize)
    - toPx(styles.getPropertyValue('--admin-content-bottom'), rootFontSize);
  const chrome = panel.getBoundingClientRect().top - page.getBoundingClientRect().top;

  return {
    available,
    panelSpace: available - chrome,
    required: Math.max(MIN_PANEL_PX, available * MIN_PANEL_SHARE),
  };
};

/**
 * Returns true when the cockpit page should give its panel a finite height
 * and let it own vertical scrolling. `eligible` is the viewport media query;
 * the measurement then guards against chrome that would crowd the panel
 * (enlarged display sizes, publish banner, alerts, an open overview).
 */
const useWorkspaceScrollOwner = (pageRef, eligible) => {
  const [fits, setFits] = useState(true);

  const evaluate = useCallback(() => {
    const workspace = eligible ? measureWorkspace(pageRef.current) : null;
    if (!workspace) return;

    setFits((current) => {
      const margin = current ? 0 : HYSTERESIS_PX;
      const next = workspace.panelSpace >= workspace.required + margin;
      return next === current ? current : next;
    });
  }, [eligible, pageRef]);

  // Chrome changes with every render (alerts, live metrics, the overview).
  useLayoutEffect(() => {
    evaluate();
  });

  // ...and with the viewport, display size, or wrapping that does not re-render.
  useEffect(() => {
    const page = pageRef.current;
    if (!eligible || !page) return undefined;

    window.addEventListener('resize', evaluate);
    const created = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(evaluate);
    const observer = typeof created?.observe === 'function' ? created : null;
    Array.from(page.children)
      .filter((child) => !child.hasAttribute('data-cockpit-panel'))
      .forEach((child) => observer?.observe(child));

    return () => {
      window.removeEventListener('resize', evaluate);
      observer?.disconnect?.();
    };
  }, [eligible, evaluate, pageRef]);

  return eligible && fits;
};

export default useWorkspaceScrollOwner;
